import React, { useMemo, useState } from 'react';
import {
  Box,
  Popover,
  Button,
  IconButton,
  InputBase,
  Avatar,
  Typography,
  CircularProgress,
  Divider,
  Tooltip,
  ButtonBase,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useActiveOrg } from '../contexts/OrgContext';

// Shell-consistent tokens — clean slate surfaces, orange only as the accent.
const ORANGE = '#FF6600';
const ORANGE_DK = '#C2410C';
const ORANGE_SOFT = 'rgba(255,102,0,0.10)';
const TEXT = '#0F172A';
const TEXT_DIM = '#64748B';
const BORDER = '#E6EAF0';
const HOVER = '#F1F5F9';
const SURFACE = '#FFFFFF';
const FONT = '"Inter", "Roboto", "Helvetica", "Arial", sans-serif';
const FOCUS = { '&:focus-visible': { outline: `2px solid ${ORANGE}`, outlineOffset: 2 } };

/**
 * Deterministic HSL color from an arbitrary string. Keeps per-tenant avatar
 * colors stable across page loads without needing a stored palette.
 */
function colorFromString(str) {
  if (!str) return 'hsl(20, 80%, 55%)';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) & 0xffffffff;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 60%, 46%)`;
}

function initialOf(name) {
  if (!name) return '?';
  const trimmed = String(name).trim();
  if (!trimmed) return '?';
  return trimmed[0].toUpperCase();
}

function OrgAvatar({ name, size = 28, active = false }) {
  const bg = colorFromString(name);
  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        bgcolor: bg,
        fontSize: size * 0.46,
        fontWeight: 700,
        color: '#fff',
        borderRadius: '9px',
        boxShadow: active ? `0 0 0 2px ${ORANGE}` : 'none',
        transition: 'box-shadow 0.15s ease',
      }}
      variant="rounded"
    >
      {initialOf(name)}
    </Avatar>
  );
}

/**
 * Organization switcher for account members (account admins / multi-org users).
 * Popover-based picker with search + per-org avatar. On switch the parent
 * OrgProvider re-mints the JWT scoped to the chosen org and the page reloads to
 * flush per-origin iframe state. Hidden for the super admin (platform operator).
 */
const OrgSwitcher = () => {
  const { orgs, activeOrg, activeOrgId, switchOrg, loadingOrgs, refreshOrgs, isSuperAdmin } = useActiveOrg();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [query, setQuery] = useState('');
  const [switchingId, setSwitchingId] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    setQuery('');
    setErrorMsg(null);
    if (orgs.length === 0) {
      refreshOrgs();
    }
  };
  const handleClose = () => setAnchorEl(null);

  const filteredOrgs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter((o) => {
      const name = (o.name || '').toLowerCase();
      const ident = (o.identifier || '').toLowerCase();
      return name.includes(q) || ident.includes(q);
    });
  }, [orgs, query]);

  const handleSwitch = async (orgId) => {
    if (String(orgId) === String(activeOrgId)) {
      handleClose();
      return;
    }
    try {
      setSwitchingId(orgId);
      setErrorMsg(null);
      await switchOrg(orgId);
      handleClose();
      // Hard reload flushes the per-origin localStorage/IndexedDB state that
      // embedded CISO / OpenMetadata iframes hold.
      window.location.reload();
    } catch (err) {
      setErrorMsg(err.response?.data?.error || 'Switch failed');
    } finally {
      setSwitchingId(null);
    }
  };

  // The super admin is a platform operator, not an operational member of any
  // tenant — it manages tenants from the Tenant Hub and never "switches" into
  // one, so the switcher is hidden entirely. Account members see it when they
  // have more than one org to move between; one or zero orgs has nothing to
  // switch between.
  if (isSuperAdmin || orgs.length <= 1) {
    return null;
  }

  const displayName = activeOrg?.name || (activeOrgId ? `Org ${String(activeOrgId).slice(0, 8)}` : 'Select organization');
  const displayIdent = activeOrg?.identifier || (activeOrgId ? 'no metadata' : 'switch to get started');

  return (
    <Box>
      <Tooltip title={open ? '' : 'Switch tenant'} arrow enterDelay={400} disableInteractive>
        <ButtonBase
          onClick={handleOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pl: 0.75,
            pr: 1,
            py: 0.5,
            borderRadius: '10px',
            border: `1px solid ${open ? ORANGE : BORDER}`,
            bgcolor: open ? ORANGE_SOFT : SURFACE,
            transition: 'background-color 0.15s ease, border-color 0.15s ease',
            '&:hover': { bgcolor: HOVER, borderColor: open ? ORANGE : '#D7DDE7' },
            ...FOCUS,
            minWidth: 0,
          }}
        >
          <OrgAvatar name={displayName} size={28} active={Boolean(activeOrgId)} />
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              alignItems: 'flex-start',
              textAlign: 'left',
              minWidth: 0,
              maxWidth: 180,
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: '0.82rem',
                fontWeight: 600,
                color: TEXT,
                lineHeight: 1.25,
                letterSpacing: '-0.01em',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {displayName}
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: '0.7rem',
                fontWeight: 500,
                color: TEXT_DIM,
                lineHeight: 1.25,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                letterSpacing: '0.01em',
              }}
            >
              {displayIdent}
            </Typography>
          </Box>
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 18,
              color: TEXT_DIM,
              transition: 'transform 0.2s ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          />
        </ButtonBase>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 360,
              maxHeight: 520,
              borderRadius: '14px',
              boxShadow: '0 12px 32px rgba(15, 23, 42, 0.16)',
              border: `1px solid ${BORDER}`,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, pt: 1.75, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', fontWeight: 700, color: TEXT, letterSpacing: '-0.01em', lineHeight: 1.3 }}>
              Switch organization
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.7rem', color: TEXT_DIM, lineHeight: 1.3 }}>
              Switch between your organizations
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} aria-label="Close" sx={{ p: 0.5, color: TEXT_DIM, ...FOCUS }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {/* Search */}
        {orgs.length > 4 && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.75,
                borderRadius: '10px',
                bgcolor: HOVER,
                border: `1px solid ${BORDER}`,
                transition: 'border-color .15s ease, background-color .15s ease',
                '&:focus-within': { borderColor: ORANGE, bgcolor: '#fff', boxShadow: `0 0 0 3px ${ORANGE_SOFT}` },
              }}
            >
              <SearchIcon sx={{ fontSize: 16, color: TEXT_DIM }} />
              <InputBase
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name or identifier…"
                autoFocus
                sx={{ flex: 1, fontFamily: FONT, fontSize: '0.8125rem', fontWeight: 500, color: TEXT, letterSpacing: '-0.005em' }}
                inputProps={{ 'aria-label': 'Search organizations' }}
              />
              {query && (
                <IconButton size="small" onClick={() => setQuery('')} sx={{ p: 0.25, color: TEXT_DIM }} aria-label="Clear search">
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Box>
          </Box>
        )}

        <Divider sx={{ borderColor: BORDER }} />

        {/* Org list */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            py: 0.5,
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: 8 },
          }}
        >
          {loadingOrgs && orgs.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={20} sx={{ color: ORANGE }} />
            </Box>
          )}

          {!loadingOrgs && orgs.length === 0 && (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <BusinessIcon sx={{ fontSize: 32, color: '#CBD5E1', mb: 0.5 }} />
              <Typography sx={{ fontFamily: FONT, fontSize: '0.875rem', fontWeight: 600, color: TEXT, letterSpacing: '-0.01em' }}>
                No organizations yet
              </Typography>
              <Typography sx={{ fontFamily: FONT, display: 'block', fontSize: '0.75rem', color: TEXT_DIM, mb: 1.5 }}>
                Create your first tenant to get started.
              </Typography>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  handleClose();
                  navigate('/organizations/new');
                }}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
              >
                Create organization
              </Button>
            </Box>
          )}

          {!loadingOrgs && orgs.length > 0 && filteredOrgs.length === 0 && (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', color: TEXT_DIM }}>
                No match for &quot;{query}&quot;
              </Typography>
            </Box>
          )}

          {filteredOrgs.map((o) => {
            const isActive = String(o.id) === String(activeOrgId);
            const isSwitchingThis = switchingId === o.id;
            return (
              <ButtonBase
                key={o.id}
                onClick={() => handleSwitch(o.id)}
                disabled={isSwitchingThis}
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 2,
                  py: 1.1,
                  borderLeft: `3px solid ${isActive ? ORANGE : 'transparent'}`,
                  bgcolor: isActive ? ORANGE_SOFT : 'transparent',
                  textAlign: 'left',
                  transition: 'background-color 0.12s ease',
                  '&:hover': { bgcolor: isActive ? ORANGE_SOFT : HOVER },
                  '&:focus-visible': { outline: `2px solid ${ORANGE}`, outlineOffset: '-2px' },
                }}
              >
                <OrgAvatar name={o.name} size={32} active={isActive} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontSize: '0.85rem',
                        fontWeight: isActive ? 700 : 600,
                        color: isActive ? ORANGE_DK : TEXT,
                        letterSpacing: '-0.01em',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 200,
                      }}
                      title={o.name}
                    >
                      {o.name}
                    </Typography>
                    {isActive && <CheckIcon sx={{ fontSize: 14, color: ORANGE }} />}
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      display: 'block',
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      color: TEXT_DIM,
                      lineHeight: 1.3,
                      letterSpacing: '0.01em',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 220,
                      mb: 0.5,
                    }}
                    title={o.identifier}
                  >
                    {o.identifier}
                  </Typography>
                </Box>
                {isSwitchingThis && <CircularProgress size={16} sx={{ ml: 0.5, color: ORANGE }} />}
              </ButtonBase>
            );
          })}
        </Box>

        {errorMsg && (
          <Box sx={{ px: 2, py: 1, bgcolor: '#FEF2F2', borderTop: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.7rem', color: '#DC2626', fontWeight: 600, letterSpacing: '0.01em' }}>
              {errorMsg}
            </Typography>
          </Box>
        )}

      </Popover>
    </Box>
  );
};

export default OrgSwitcher;
